import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('safetydesk81_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-redirect on 401
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('safetydesk81_token');
      localStorage.removeItem('safetydesk81_user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export default api;

// Convenience endpoints
export const authApi = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  me: () => api.get('/auth/me'),
};

export const companiesApi = {
  list: (params) => api.get('/companies', { params }),
  get: (id) => api.get(`/companies/${id}`),
  create: (body) => api.post('/companies', body),
  update: (id, body) => api.patch(`/companies/${id}`, body),
  remove: (id) => api.delete(`/companies/${id}`),
};

export const visiteAlignApi = {
  downloadTemplate: () => window.open(`${API_BASE}/admin/visite-align/template?_t=${Date.now()}`, '_blank'),
  previewImport: (formData) =>
    api.post('/admin/visite-align/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
    }),
  applyImport: (payload) =>
    api.post('/admin/visite-align/apply', payload, { timeout: 180000 }),
};

export const sorveglianzaExportApi = {
  download: (params) => downloadFile('/visite/sanitario/export', params),
};

export const workersApi = {
  list: (params) => api.get('/workers', { params }),
  get: (id) => api.get(`/workers/${id}`),
  create: (body) => api.post('/workers', body),
  update: (id, body) => api.patch(`/workers/${id}`, body),
  remove: (id) => api.delete(`/workers/${id}`),
  reassignCompany: (wid, companyId) =>
    api.post(`/workers/${wid}/reassign-company`, { companyId }),
  mergePreview: (principal_id, duplicate_ids) =>
    api.post('/admin/workers/merge', { principal_id, duplicate_ids, confirm: false }),
  mergeApply: (principal_id, duplicate_ids) =>
    api.post('/admin/workers/merge', { principal_id, duplicate_ids, confirm: true }),
};

export const duplicatesFormazioneApi = {
  preview: () => api.get('/admin/formazione/duplicates/preview'),
  apply: () => api.post('/admin/formazione/duplicates/apply'),
};
export const orphanFormazioneApi = {
  preview: () => api.get('/admin/formazione/orphans/preview'),
  apply: () => api.post('/admin/formazione/orphans/apply'),
  rollback: (backupCollection) =>
    api.post(`/admin/formazione/orphans/rollback/${backupCollection}`),
};

export const privatiCleanupApi = {
  preview: () => api.get('/admin/privati/cleanup-dismessi/preview'),
  apply: () => api.post('/admin/privati/cleanup-dismessi/apply'),
  rollback: (backupCollection) =>
    api.post(`/admin/privati/cleanup-dismessi/rollback/${backupCollection}`),
};

const crud = (path) => ({
  list: (params) => api.get(`/${path}`, { params }),
  create: (body) => api.post(`/${path}`, body),
  update: (id, body) => api.patch(`/${path}/${id}`, body),
  remove: (id) => api.delete(`/${path}/${id}`),
});

export const formazioneApi = {
  ...crud('formazione'),
  aggregated: (params) => api.get('/formazione/aggregated', { params }),
  daFare: (params) => api.get('/formazione/da-fare', { params }),
  daFareExport: (params) => downloadFile('/formazione/da-fare/export.xlsx', params),
  todoByRoles: (params) => api.get('/formazione/todo-by-roles', { params }),
  todoByRolesExport: (params) => downloadFile('/formazione/todo-by-roles/export.xlsx', params),
  categories: () => api.get('/formazione/categories'),
  titoliDaVerificare: () => api.get('/formazione/titoli-da-verificare'),
  exportXlsx: (params) => downloadFile('/formazione/export.xlsx', params),
};
export const visiteApi = {
  ...crud('visite'),
  sanitario: (params) => api.get('/visite/sanitario', { params }),
};
export const dpiApi = crud('dpi');
export const haccpApi = crud('haccp');
export const sicurezzaApi = crud('sicurezza');
export const calendarApi = crud('calendar');
export const comunicazioniApi = {
  list: () => api.get('/comunicazioni'),
  create: (body) => api.post('/comunicazioni', body),
  markRead: (id) => api.patch(`/comunicazioni/${id}/read`),
};
export const dashboardApi = {
  stats: () => api.get('/dashboard/stats'),
  upcoming: (days = 30) => api.get('/dashboard/upcoming-expirations', { params: { days } }),
  charts: () => api.get('/dashboard/charts'),
};
export const migrationApi = { status: () => api.get('/migration/status') };

export const notificationsApi = {
  config: () => api.get('/notifications/config'),
  listRules: () => api.get('/notifications/rules'),
  createRule: (body) => api.post('/notifications/rules', body),
  updateRule: (id, body) => api.patch(`/notifications/rules/${id}`, body),
  deleteRule: (id) => api.delete(`/notifications/rules/${id}`),
  runRule: (id) => api.post(`/notifications/rules/${id}/run`),
  previewRule: (id) => api.get(`/notifications/rules/${id}/preview`),
  runs: () => api.get('/notifications/runs'),
  testSmtp: (to) => api.post('/notifications/test-smtp', { to }),
};

// Helper to trigger a file download from a backend GET endpoint.
//
// Strategy:
//   1. Desktop (top-level window, no iOS): fetch as blob and use <a download>.
//   2. Mobile / in-app preview / iOS: navigate to an absolute URL with the
//      auth token in the ?token= query string. The browser handles the
//      download natively via Content-Disposition, which works on Samsung
//      Internet, Android WebView, iOS Safari and in-app preview iframes
//      where blob URLs and <a download> are blocked.
export const downloadFile = async (path, params) => {
  // Serializza params in query string se forniti
  let fullPath = path;
  if (params && Object.keys(params).length > 0) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') qs.append(k, String(v));
    });
    const sep = path.includes('?') ? '&' : '?';
    fullPath = `${path}${sep}${qs.toString()}`;
  }
  const token = localStorage.getItem('safetydesk81_token');
  const ua = navigator.userAgent || '';
  const isMobile = /Android|iPad|iPhone|iPod|Mobile/i.test(ua);
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isFramed = (() => {
    try { return window.self !== window.top; } catch { return true; }
  })();

  if ((isMobile || isFramed || isIOS) && token) {
    const sep = fullPath.includes('?') ? '&' : '?';
    const absolute = `${API_BASE}${fullPath}${sep}token=${encodeURIComponent(token)}`;
    const a = document.createElement('a');
    a.href = absolute;
    a.target = '_top';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => a.remove(), 0);
    return;
  }

    // Desktop path: fetch as blob and trigger a synthetic download.
    let apiPath = path;
    if (params && Object.keys(params).length > 0) {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v != null && v !== '') qs.append(k, String(v));
      });
      const sep = path.includes('?') ? '&' : '?';
      apiPath = `${path}${sep}${qs.toString()}`;
    }
    const res = await api.get(apiPath, { responseType: 'blob' });
  const cd = res.headers['content-disposition'] || '';
  let filename = 'download';
  const m1 = cd.match(/filename\*=(?:UTF-8'')?"?([^";]+)"?/i);
  const m2 = cd.match(/filename="?([^";]+)"?/i);
  if (m1) {
    try { filename = decodeURIComponent(m1[1]); } catch { filename = m1[1]; }
  } else if (m2) {
    filename = m2[1];
  }

  const blob = res.data;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 200);
};

export const reportsApi = {
  preview: (companyId) => api.get('/reports/preview', { params: companyId && companyId !== 'all' ? { companyId } : {} }),
  companyExcel: (id) => downloadFile(`/reports/company/${id}/excel`),
  companyZip: (id) => downloadFile(`/reports/company/${id}/zip`),
  bulkExcel: () => downloadFile('/reports/bulk/excel'),
  workerExcel: (id) => downloadFile(`/reports/worker/${id}/excel`),
};

export const uploadsApi = {
  list: (params) => api.get('/uploads', { params }),
  upload: (formData) => api.post('/uploads', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  download: (id) => downloadFile(`/uploads/${id}/download`),
  remove: (id) => api.delete(`/uploads/${id}`),
};

export const bulkApi = {
  formazione: (body) => api.post('/bulk/formazione', body),
  visite: (body) => api.post('/bulk/visite', body),
};

export const usersApi = {
  list: () => api.get('/users'),
  create: (body) => api.post('/users', body),
  update: (id, body) => api.patch(`/users/${id}`, body),
  remove: (id) => api.delete(`/users/${id}`),
  sendWelcome: (id) => api.post(`/users/${id}/send-welcome`),
};

export const auditApi = {
  list: (params) => api.get('/audit', { params }),
  recent: (limit = 20) => api.get('/audit/recent', { params: { limit } }),
};

export const importApi = {
  preview: (formData) => api.post('/bulk/import-workers/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  confirm: (workers) => api.post('/bulk/import-workers/confirm', { workers }),
};

export const workersImportApi = {
  exportXlsx: (params) => downloadFile('/workers-io/export.xlsx', params),
  downloadTemplate: (companyId) => downloadFile('/workers-io/import-template.xlsx', companyId ? { companyId } : {}),
  preview: (formData) => api.post('/workers-io/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  apply: (companyId, dryRunToken, row_numbers) => api.post('/workers-io/import/apply', { companyId, dryRunToken, row_numbers }),
};

export const companiesImportApi = {
  preview: (formData) => api.post('/companies/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  confirm: (sessionId) => api.post('/companies/import/confirm', { sessionId }),
  downloadReport: (sid) => downloadFile(`/companies/import/report/${sid}/xlsx`),
};

export const companiesExportApi = {
  downloadXlsx: (status = 'attive') => downloadFile(`/companies/export/xlsx?status=${encodeURIComponent(status)}`),
};

export const sicurezzaImportApi = {
  preview: (formData) => api.post('/sicurezza/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  confirm: (sessionId) => api.post('/sicurezza/import/confirm', { sessionId }),
  aggregated: () => api.get('/sicurezza/aggregated'),
};

export const mailingApi = {
  preview: (segment) => api.post('/mailing/preview', { segment }),
  send: (segment, subject, body_html, extra = {}) => api.post('/mailing/send', { segment, subject, body_html, bcc_self: false, ...extra }),
  runs: (limit = 30) => api.get('/mailing/runs', { params: { limit } }),
  atecoOptions: () => api.get('/mailing/ateco-options'),
  filterOptions: () => api.get('/mailing/filter-options'),
};

export const profilingApi = {
  preview: (body = { useAI: true }) => api.post('/companies/profile/auto/preview', body),
  apply: (applications) => api.post('/companies/profile/auto/apply', { applications }),
};

export const sopralluoghiApi = {
  list: (params) => api.get('/sopralluoghi', { params }),
  types: () => api.get('/sopralluoghi/types'),
  create: (body) => api.post('/sopralluoghi', body),
  update: (id, body) => api.patch(`/sopralluoghi/${id}`, body),
  remove: (id) => api.delete(`/sopralluoghi/${id}`),
  checkAlerts: () => api.post('/sopralluoghi/check-alerts'),
  complete: (id, body) => api.post(`/sopralluoghi/${id}/complete`, body),
  deleteHistory: (id, hid) => api.delete(`/sopralluoghi/${id}/history/${hid}`),
};

export const dismissalApi = {
  dismissWorker: (wid, body = {}) => api.post(`/workers/${wid}/dismiss`, body),
  rehireWorker: (wid, body) => api.post(`/workers/${wid}/rehire`, body),
  dismissCompany: (cid, body = {}) => api.post(`/companies/${cid}/dismiss`, body),
  reactivateCompany: (cid, body = {}) => api.post(`/companies/${cid}/reactivate`, body),
};

export const rolesApi = {
  catalog: () => api.get('/roles/catalog'),
  updateWorkerRuoli: (wid, ruoli, mode = 'replace') =>
    api.patch(`/workers/${wid}/ruoli`, { ruoli, mode }),
  downloadTemplate: () => downloadFile('/admin/roles/import/template.xlsx'),
  previewImport: (formData) =>
    api.post('/admin/roles/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  applyImport: (body) => api.post('/admin/roles/import/apply', body),
  applyStatus: (opId) => api.get(`/admin/roles/import/status/${opId}`),
};

export const contrattiServiziApi = {
  previewImport: (formData) =>
    api.post('/admin/contratti-servizi/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  previewStatus: (token) => api.get(`/admin/contratti-servizi/preview-status/${token}`),
  previewRows: (token) => api.get(`/admin/contratti-servizi/preview-rows/${token}`),
  downloadReport: (token) => downloadFile(`/admin/contratti-servizi/preview-report/${token}`),
  applyImport: (body) => api.post('/admin/contratti-servizi/apply', body),
  applyStatus: (opId) => api.get(`/admin/contratti-servizi/apply-status/${opId}`),
  rollback: (opId) => api.post(`/admin/contratti-servizi/rollback/${opId}`),
  cleanupTipologia: (dryRun = true) => api.post(`/admin/visite/cleanup-tipologia?dry_run=${dryRun}`),
};

export const formazioneAlignApi = {
  downloadTemplate: () => downloadFile('/admin/formazione-align/template.xlsx'),
  previewImport: (formData) =>
    api.post('/admin/formazione-align/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  previewStatus: (token) => api.get(`/admin/formazione-align/preview-status/${token}`),
  downloadReport: (token) => downloadFile(`/admin/formazione-align/preview-report/${token}`),
  downloadApplyReport: (opId) => downloadFile(`/admin/formazione-align/apply-report/${opId}`),
  applyImport: (body) => api.post('/admin/formazione-align/apply', body),
  applyStatus: (opId) => api.get(`/admin/formazione-align/status/${opId}`),
};

export const presenzeApi = {
  downloadTemplate: () => downloadFile('/admin/presenze/template.xlsx'),
  preview: (formData) =>
    api.post('/admin/presenze/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  apply: (body) => api.post('/admin/presenze/apply', body),
  status: (opId) => api.get(`/admin/presenze/status/${opId}`),
};

export const privatiFileMergeApi = {
  preview: (formData) =>
    api.post('/admin/duplicates/merge-from-file/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  apply: (body) => api.post('/admin/duplicates/merge-from-file/apply', body),
  status: (opId) => api.get(`/admin/duplicates/merge-from-file/status/${opId}`),
};

export const registriesApi = {
  templates: (params) => api.get('/registries/templates', { params }),
  createTemplate: (body) => api.post('/registries/templates', body),
  companyAssignments: (cid) => api.get(`/registries/companies/${cid}/assignments`),
  assign: (cid, body) => api.post(`/registries/companies/${cid}/assignments`, body),
  updateAssignment: (aid, body) => api.patch(`/registries/assignments/${aid}`, body),
  deactivateAssignment: (aid) => api.delete(`/registries/assignments/${aid}`),
  entries: (params) => api.get('/registries/entries', { params }),
  createEntry: (body) => api.post('/registries/entries', body),
  updateEntry: (eid, body) => api.patch(`/registries/entries/${eid}`, body),
  deleteEntry: (eid) => api.delete(`/registries/entries/${eid}`),
  dashboard: (params) => api.get('/registries/dashboard', { params }),
  nonConformities: (params) => api.get('/registries/non-conformities', { params }),
  updateNC: (nid, body) => api.patch(`/registries/non-conformities/${nid}`, body),
};
